import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { commonErrorResponses, emptyEnvelopeSchema, requiredPayloadEnvelope, } from '../envelope.js';
/** 썸네일 생성·조회 상태다. `ready`일 때만 previewUrl이 채워질 수 있다. */
const thumbnailStatusSchema = z.enum(['none', 'pending', 'ready', 'failed']);
/** 사용자 워크스페이스 파일·폴더 한 행의 HTTP 표현이다. */
export const workspaceFileSchema = z.object({
    /** WorkspaceFile 행 id다. */
    id: z.string(),
    /** v1은 사용자 개인 보관함만 지원하며 항상 `user`다. */
    ownerKind: z.literal('user'),
    /** 소유 사용자 id다. */
    ownerId: z.string(),
    /** `file` 또는 `folder`다. */
    kind: z.enum(['file', 'folder']),
    /** 부모 폴더 id다. 루트면 null이다. */
    parentId: z.string().nullable(),
    /** 탐색기에 보이는 표시 이름이다. */
    name: z.string(),
    /** 업로드 원본 파일명이다. 폴더면 null이다. */
    originalFilename: z.string().nullable(),
    /** Blob 저장 경로다. 폴더·미확정 업로드면 null이다. */
    storagePath: z.string().nullable(),
    /** MIME 타입이다. 폴더면 null이다. */
    mimeType: z.string().nullable(),
    /** 바이트 크기 문자열이다. DB bigint를 JSON으로 옮긴 값이다. 폴더면 null이다. */
    fileSize: z.string().nullable(),
    /** true면 프라이빗 보관함, false면 퍼블릭 보관함이다. 형제 이름·이동은 같은 isPrivate 안에서만 허용한다. */
    isPrivate: z.boolean(),
    /** 썸네일 파이프라인 상태다. */
    thumbnailStatus: thumbnailStatusSchema,
    /** 생성된 썸네일 Blob 경로다. 없으면 null이다. */
    thumbnailPath: z.string().nullable(),
    /** 목록·폴링용 썸네일 미리보기 URL이다. private 파일은 SAS URL일 수 있다. */
    previewUrl: z.string().nullable(),
    /** 미디어 재생 횟수 집계다. */
    playCount: z.number().int(),
    /** 행 생성 시각의 ISO 문자열이다. */
    createdAt: z.string(),
    /** 행 마지막 수정 시각의 ISO 문자열이다. */
    updatedAt: z.string(),
});
/** 목록·브레드크럼 조회 공통 쿼리다. */
const workspaceListQuerySchema = z.object({
    /** 조회할 보관함이 프라이빗인지 여부다. */
    isPrivate: z.coerce.boolean(),
    /** 열어볼 부모 폴더 id다. 루트면 생략한다. */
    parentId: z.string().optional(),
});
/** 휴지통에 있는 파일·폴더 행이다. */
export const workspaceTrashFileSchema = workspaceFileSchema.extend({
    /** 휴지통으로 이동한 시각의 ISO 문자열이다. */
    deletedAt: z.string(),
});
const c = initContract();
/** user-api — 개인 워크스페이스(보관함) 파일 탐색·업로드·이동·휴지통. */
export const userWorkspaceContract = c.router({
    /** 지정 폴더(또는 루트)의 자식 파일·폴더 목록을 조회한다. */
    listChildren: {
        method: 'GET',
        path: '/workspace/files',
        query: workspaceListQuerySchema,
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 현재 폴더의 자식 항목 목록이다. */
                items: z.array(workspaceFileSchema),
            })),
            ...commonErrorResponses,
        },
    },
    /** 현재 위치까지의 폴더 경로(브레드크럼)를 조회한다. */
    getBreadcrumb: {
        method: 'GET',
        path: '/workspace/files/breadcrumb',
        query: workspaceListQuerySchema,
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 루트부터 현재 폴더까지의 경로 조각이다. */
                items: z.array(z.object({
                    /** 폴더 id다. 루트 조각이면 null이다. */
                    id: z.string().nullable(),
                    /** 브레드크럼에 보이는 이름이다. */
                    name: z.string(),
                })),
            })),
            ...commonErrorResponses,
        },
    },
    /** 현재 위치에서 허용되는 작업(업로드·폴더 생성 등)을 조회한다. */
    getCapabilities: {
        method: 'GET',
        path: '/workspace/files/capabilities',
        query: workspaceListQuerySchema,
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 파일 업로드를 허용하면 true다. */
                canUpload: z.boolean(),
                /** 폴더 생성을 허용하면 true다. */
                canCreateFolder: z.boolean(),
                /** 이름 변경을 허용하면 true다. */
                canRename: z.boolean(),
                /** 다른 폴더로 이동을 허용하면 true다. */
                canMove: z.boolean(),
                /** 휴지통 이동을 허용하면 true다. */
                canTrash: z.boolean(),
                /** 다운로드·읽기 URL 발급을 허용하면 true다. */
                canDownload: z.boolean(),
            })),
            ...commonErrorResponses,
        },
    },
    /** Blob 직접 업로드용 SAS URL과 임시 fileId를 발급한다. */
    createUploadIntent: {
        method: 'POST',
        path: '/workspace/files/upload-intent',
        body: z.object({
            /** 업로드할 원본 파일명이다. */
            fileName: z.string(),
            /** MIME 타입이다. */
            mimeType: z.string(),
            /** 바이트 크기다. 단일 파일 상한을 넘으면 거절한다. */
            fileSize: z.number().int().positive(),
            /** 프라이빗 보관함에 넣을지 여부다. */
            isPrivate: z.boolean(),
            /** 대상 부모 폴더 id다. 루트면 null 또는 생략한다. */
            parentId: z.string().nullable().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** confirmUpload에 넘길 WorkspaceFile id다. */
                fileId: z.string(),
                /** Blob에 PUT할 SAS URL이다. */
                uploadUrl: z.string(),
                /** 업로드 요청에 붙일 추가 헤더다. */
                uploadHeaders: z.record(z.string()).optional(),
                /** SAS 만료 시각의 ISO 문자열이다. */
                expiresAt: z.string(),
            })),
            ...commonErrorResponses,
        },
    },
    /** Blob 업로드 완료를 서버에 확정하고 메타데이터를 저장한다. */
    confirmUpload: {
        method: 'POST',
        path: '/workspace/files/:fileId/confirm',
        pathParams: z.object({
            /** createUploadIntent에서 받은 fileId다. */
            fileId: z.string(),
        }),
        body: z.object({
            /** 업로드한 원본 파일명이다. */
            fileName: z.string(),
            /** MIME 타입이다. */
            mimeType: z.string(),
            /** 실제 업로드된 바이트 크기다. */
            fileSize: z.number().int().positive(),
            /** 프라이빗 보관함 여부다. */
            isPrivate: z.boolean(),
            /** 부모 폴더 id다. */
            parentId: z.string().nullable().optional(),
            /** 표시 이름을 파일명과 다르게 줄 때 쓴다. */
            name: z.string().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 확정된 파일 행이다. */
                file: workspaceFileSchema,
            })),
            ...commonErrorResponses,
        },
    },
    /** 파일 읽기·다운로드용 SAS URL을 발급한다. */
    getReadUrl: {
        method: 'GET',
        path: '/workspace/files/:fileId/read-url',
        pathParams: z.object({
            /** 읽을 파일 id다. */
            fileId: z.string(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 파일 내용을 가져올 URL이다. */
                readUrl: z.string(),
                /** SAS 만료 시각이다. 퍼블릭 Blob이면 null일 수 있다. */
                expiresAt: z.string().nullable(),
            })),
            ...commonErrorResponses,
        },
    },
    /** 여러 파일의 썸네일 상태를 한 번에 폴링한다. ids는 쉼표 구분이다. */
    getThumbnailStatuses: {
        method: 'GET',
        path: '/workspace/files/thumbnail-statuses',
        query: z.object({
            /** 조회할 file id 목록이다. 쉼표로 구분한다. */
            ids: z.string(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 요청 id별 썸네일 상태다. */
                items: z.array(z.object({
                    /** 파일 id다. */
                    id: z.string(),
                    /** 썸네일 파이프라인 상태다. */
                    thumbnailStatus: thumbnailStatusSchema,
                    /** 준비됐을 때 미리보기 URL이다. */
                    previewUrl: z.string().nullable(),
                })),
            })),
            ...commonErrorResponses,
        },
    },
    /** 새 폴더를 만든다. */
    createFolder: {
        method: 'POST',
        path: '/workspace/folders',
        body: z.object({
            /** 폴더 표시 이름이다. */
            name: z.string(),
            /** 프라이빗 보관함 여부다. */
            isPrivate: z.boolean(),
            /** 부모 폴더 id다. */
            parentId: z.string().nullable().optional(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 생성된 폴더 행이다. */
                file: workspaceFileSchema,
            })),
            ...commonErrorResponses,
        },
    },
    /** 파일·폴더 표시 이름을 바꾼다. */
    renameFile: {
        method: 'PATCH',
        path: '/workspace/files/:fileId',
        pathParams: z.object({
            /** 이름을 바꿀 항목 id다. */
            fileId: z.string(),
        }),
        body: z.object({
            /** 새 표시 이름이다. */
            name: z.string(),
        }),
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 갱신된 항목 행이다. */
                file: workspaceFileSchema,
            })),
            ...commonErrorResponses,
        },
    },
    /** 선택한 항목을 다른 폴더로 이동한다. */
    moveFiles: {
        method: 'POST',
        path: '/workspace/files/move',
        body: z.object({
            /** 이동할 file·folder id 목록이다. */
            ids: z.array(z.string()).min(1),
            /** 대상 부모 폴더 id다. 루트면 null이다. */
            targetParentId: z.string().nullable(),
        }),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
    /** 선택한 항목을 휴지통으로 보낸다. */
    trashFiles: {
        method: 'POST',
        path: '/workspace/files/trash',
        body: z.object({
            /** 휴지통으로 보낼 id 목록이다. */
            ids: z.array(z.string()).min(1),
        }),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
    /** 휴지통 항목 목록을 조회한다. */
    listTrash: {
        method: 'GET',
        path: '/workspace/files/trash',
        responses: {
            200: requiredPayloadEnvelope(z.object({
                /** 휴지통에 있는 항목 목록이다. */
                items: z.array(workspaceTrashFileSchema),
            })),
            ...commonErrorResponses,
        },
    },
    /** 휴지통에서 항목을 복원한다. */
    restoreFiles: {
        method: 'POST',
        path: '/workspace/files/restore',
        body: z.object({
            /** 복원할 id 목록이다. */
            ids: z.array(z.string()).min(1),
        }),
        responses: {
            200: emptyEnvelopeSchema,
            ...commonErrorResponses,
        },
    },
});
//# sourceMappingURL=workspace.contract.js.map